import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as Icons from 'lucide-react';
import { StatusPill } from '@/components/ui/status-pill';
import { LiveStatusStrip } from '@/components/ui/live-status-strip';
import { ExceptionList } from '@/components/ui/exception-list';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
} from 'recharts';
import { canAccessRoute } from '@logisticslash/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';

const STATUS_COLORS = {
  in_transit: '#0B5FFF',
  dispatched: '#22D3EE',
  delivered: '#0C9F5A',
  draft: '#64748B',
  closed: '#0C9F5A',
  invoiced: '#8B5CF6',
  canceled: '#EF4444',
};

function formatMoney(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100);
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const [period, setPeriod] = useState('30');

  const role = user?.role;
  const canMutateLoads = role === 'admin' || role === 'dispatcher';

  const statsQ = useQuery({
    queryKey: ['overview-stats', period],
    queryFn: async () => {
      const r = await api(`/api/v1/overview/stats?days=${period}`);
      if (r.status !== 200) {
        return {
          kpis: {},
          revenueSeries: { days: [], amountsCents: [] },
          loadStatusCounts: {},
          iftaByJurisdiction: [],
        };
      }
      return r.data;
    },
  });

  const loadsQ = useQuery({
    queryKey: ['loads'],
    queryFn: async () => {
      const r = await api('/api/v1/loads');
      if (r.status !== 200) throw new Error('loads');
      return r.data.loads ?? [];
    },
  });

  const setQ = useQuery({
    queryKey: ['settlements'],
    queryFn: async () => {
      const r = await api('/api/v1/settlements');
      if (r.status !== 200) return [];
      return r.data.settlements ?? [];
    },
  });

  const stats = statsQ.data;
  const loads = loadsQ.data ?? [];
  const settlements = setQ.data ?? [];

  const revenueData = useMemo(() => {
    const days = stats?.revenueSeries?.days ?? [];
    const amounts = stats?.revenueSeries?.amountsCents ?? [];
    return days.map((day, i) => ({
      day: String(day).slice(5) || day,
      revenue: (amounts[i] ?? 0) / 100,
    }));
  }, [stats]);

  const statusData = useMemo(() => {
    const counts = stats?.loadStatusCounts ?? {};
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        name: name.replace(/_/g, ' '),
        key: name,
        value,
        color: STATUS_COLORS[name] || '#64748B',
      }));
  }, [stats]);

  const iftaData = useMemo(() => {
    return (stats?.iftaByJurisdiction ?? []).map((row) => ({
      state: row.jurisdiction,
      miles: row.netTaxDue ?? row.miles ?? 0,
    }));
  }, [stats]);

  const revenueTotal = useMemo(() => {
    return (stats?.revenueSeries?.amountsCents ?? []).reduce((a, b) => a + (b || 0), 0);
  }, [stats]);

  const atRisk = useMemo(() => {
    return loads
      .filter(
        (l) =>
          l.status === 'in_transit' &&
          l.deliveryDate &&
          Date.parse(l.deliveryDate) < Date.now()
      )
      .slice(0, 6)
      .map((l) => ({
        title: `Load #${l.loadNumber}`,
        description: 'Past delivery date while still in transit',
        status: 'danger',
        action: 'Review',
        onAction: () => navigate('/loads'),
      }));
  }, [loads, navigate]);

  const reviewQueue = useMemo(() => {
    return settlements
      .filter((s) => s.needsReview && !s.reviewedAt)
      .slice(0, 6)
      .map((s) => ({
        title: s.driverName ?? 'Driver settlement',
        description: s.reviewReason ?? 'Needs review',
        status: 'warn',
        action: 'Review',
        onAction: () => navigate('/settlements'),
      }));
  }, [settlements, navigate]);

  const exceptions = useMemo(() => [...atRisk, ...reviewQueue].slice(0, 8), [atRisk, reviewQueue]);
  const recent = useMemo(() => loads.slice(0, 6), [loads]);

  const metrics = [
    {
      label: 'Active Loads',
      value: String(stats?.kpis?.inTransit ?? 0),
      icon: 'Package',
      color: 'text-primary',
    },
    {
      label: 'Need Review',
      value: String(stats?.kpis?.needReview ?? 0),
      icon: 'AlertTriangle',
      color: 'text-yellow-500',
    },
    {
      label: `Delivered (${period}d)`,
      value: String(stats?.kpis?.delivered ?? 0),
      icon: 'CheckCircle2',
      color: 'text-green-500',
    },
    {
      label: `Revenue (${period}d)`,
      value: formatMoney(revenueTotal),
      icon: 'DollarSign',
      color: 'text-emerald-500',
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {company?.name ?? 'Overview Dashboard'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {user?.displayName
                ? `${user.displayName} · ${user.role ?? ''}`
                : 'Real-time operational insights and performance metrics'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 p-1 bg-muted rounded-lg">
              {['7', '30', '90'].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setPeriod(d)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    period === d
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            {canMutateLoads ? (
              <Button onClick={() => navigate('/loads')}>
                <Icons.Plus className="h-4 w-4 mr-2" />
                Create Load
              </Button>
            ) : null}
          </div>
        </div>

        <LiveStatusStrip
          metrics={[
            {
              label: 'In Transit',
              value: String(stats?.kpis?.inTransit ?? 0),
              className: 'text-primary',
            },
            {
              label: 'Need Review',
              value: String(stats?.kpis?.needReview ?? 0),
              className: 'text-yellow-500',
            },
            {
              label: 'Draft',
              value: String(stats?.kpis?.draftOpen ?? 0),
              className: 'text-muted-foreground',
            },
            {
              label: 'Delivered+',
              value: String(stats?.kpis?.delivered ?? 0),
              className: 'text-green-500',
            },
          ]}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric) => {
            const Icon = Icons[metric.icon] || Icons.Circle;
            return (
              <Card key={metric.label}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardDescription className="text-sm font-medium">{metric.label}</CardDescription>
                  <Icon className={`h-4 w-4 ${metric.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold data-text">
                    {statsQ.isLoading ? '—' : metric.value}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Revenue Trend</CardTitle>
                  <CardDescription>Last {period} days</CardDescription>
                </div>
                {revenueData.length > 0 ? (
                  <StatusPill status="active" showDot>
                    Live
                  </StatusPill>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              {revenueData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-16 text-center">
                  No revenue in this period yet
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0B5FFF" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#0B5FFF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#0B5FFF"
                      strokeWidth={2}
                      fill="url(#revenueGrad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Load Status</CardTitle>
              <CardDescription>Current distribution</CardDescription>
            </CardHeader>
            <CardContent>
              {statusData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-16 text-center">No loads yet</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {statusData.map((entry) => (
                          <Cell key={entry.key} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-4">
                    {statusData.map((s) => (
                      <div key={s.key} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                          <span className="text-muted-foreground capitalize">{s.name}</span>
                        </div>
                        <span className="font-medium font-mono">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Loads</CardTitle>
              <CardDescription>Latest shipments</CardDescription>
            </CardHeader>
            <CardContent>
              {recent.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No loads yet — create one to populate this list.
                </p>
              ) : (
                <div className="space-y-3">
                  {recent.map((load) => (
                    <div
                      key={load.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Icons.Package className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium data-text text-sm">#{load.loadNumber}</p>
                          <p className="text-xs text-muted-foreground">
                            {[load.shipperName, load.consigneeName].filter(Boolean).join(' → ') ||
                              '—'}
                          </p>
                        </div>
                      </div>
                      <StatusPill status={load.status} />
                    </div>
                  ))}
                </div>
              )}
              <Button variant="ghost" className="w-full mt-3" onClick={() => navigate('/loads')}>
                View all loads
                <Icons.ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Exceptions</CardTitle>
              <CardDescription>Items requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              <ExceptionList items={exceptions} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>IFTA</CardTitle>
              <CardDescription>By jurisdiction</CardDescription>
            </CardHeader>
            <CardContent>
              {iftaData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-16 text-center">
                  No IFTA rows for this period
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={iftaData} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      horizontal={false}
                    />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis
                      dataKey="state"
                      type="category"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      width={30}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="miles" fill="#0B5FFF" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                {
                  label: 'Create Load',
                  icon: 'Plus',
                  href: '/loads',
                  color: 'text-primary',
                  show: canMutateLoads,
                },
                {
                  label: 'Dispatch Board',
                  icon: 'Columns3',
                  href: '/dispatch',
                  color: 'text-blue-500',
                  show: role ? canAccessRoute(role, '/dispatch') : false,
                },
                {
                  label: 'Track Shipment',
                  icon: 'MapPinned',
                  href: '/tracking',
                  color: 'text-purple-500',
                  show: role ? canAccessRoute(role, '/tracking') : false,
                },
                {
                  label: 'New Invoice',
                  icon: 'FileText',
                  href: '/invoices',
                  color: 'text-green-500',
                  show: role ? canAccessRoute(role, '/invoices') : false,
                },
              ]
                .filter((action) => action.show)
                .map((action) => {
                  const Icon = Icons[action.icon] || Icons.Circle;
                  return (
                    <Button
                      key={action.label}
                      variant="outline"
                      className="h-24 flex-col gap-2"
                      onClick={() => navigate(action.href)}
                    >
                      <Icon className={`h-6 w-6 ${action.color}`} />
                      <span className="text-xs">{action.label}</span>
                    </Button>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      </div>
);
}
